export const GRID_COLUMNS = 12;
export const MIN_CARD_SPAN = 3;
export const CARD_HEIGHTS = ["short", "medium", "tall"] as const;

export type CardHeight = typeof CARD_HEIGHTS[number];

export const CARD_HEIGHT_PX: Record<CardHeight, number> = { short: 450, medium: 700, tall: 950 };

export const CARD_HEIGHT_CLASS: Record<CardHeight, string> = { short: "h-[450px]", medium: "h-[700px]", tall: "h-[950px]" };

export interface CardSize {
  w: number;
  h: CardHeight;
}

export interface CardPlacement extends CardSize {
  key: string;
  hidden: boolean;
}

export interface SavedPlacement {
  key: unknown;
  w: unknown;
  h: unknown;
  hidden: unknown;
}

interface PlaceableCard {
  key: string;
  defaultSize: CardSize;
}

export type PlacedCard<T> = CardPlacement & { card: T };

export type ItemOrders = Record<string, string[]>;

export interface CardArrangement {
  span: number;
  height: CardHeight;
}

export const parseSavedLayout = (stored: string | null): SavedPlacement[] | null => {
  if (stored === null) return null;

  try {
    const parsed: unknown = JSON.parse(stored);
    return Array.isArray(parsed)
      ? parsed.filter((entry): entry is SavedPlacement => typeof entry === "object" && entry !== null)
      : null;
  } catch {
    return null;
  }
};

export const parseItemOrders = (stored: string | null): ItemOrders => {
  if (stored === null) return {};

  try {
    const parsed: unknown = JSON.parse(stored);
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return {};

    return Object.fromEntries(
      Object.entries(parsed)
        .filter((entry): entry is [string, unknown[]] => Array.isArray(entry[1]))
        .map(([card, order]) => [card, order.filter((key): key is string => typeof key === "string")])
    );
  } catch {
    return {};
  }
};

export const arrangeItems = <T>(items: T[], order: string[] | undefined, keyOf: (item: T) => string): T[] => {
  if (!order?.length) return items;

  const rank = new Map(order.map((key, index) => [key, index]));
  const placed = items
    .filter(item => rank.has(keyOf(item)))
    .sort((a, b) => (rank.get(keyOf(a)) ?? 0) - (rank.get(keyOf(b)) ?? 0));

  return [...placed, ...items.filter(item => !rank.has(keyOf(item)))];
};

const clampSpan = (span: number) => Math.min(GRID_COLUMNS, Math.max(MIN_CARD_SPAN, span));

const clampWidth = (width: unknown, fallback: number) =>
  typeof width === "number" && Number.isFinite(width) ? clampSpan(Math.round(width)) : fallback;

const isCardHeight = (height: unknown): height is CardHeight => CARD_HEIGHTS.includes(height as CardHeight);

export const resolveLayout = <T extends PlaceableCard>(available: T[], saved: SavedPlacement[] | null): PlacedCard<T>[] => {
  const placed = new Set<string>();
  const layout: PlacedCard<T>[] = [];

  for (const placement of saved ?? []) {
    const card = available.find(candidate => candidate.key === placement.key);
    if (!card || placed.has(card.key)) continue;

    placed.add(card.key);
    layout.push({
      key: card.key,
      w: clampWidth(placement.w, card.defaultSize.w),
      h: isCardHeight(placement.h) ? placement.h : card.defaultSize.h,
      hidden: placement.hidden === true,
      card,
    });
  }

  for (const card of available) {
    if (!placed.has(card.key)) layout.push({ key: card.key, ...card.defaultSize, hidden: false, card });
  }

  return layout;
};

export const toSavedLayout = (layout: CardPlacement[]): CardPlacement[] =>
  layout.map(({ key, w, h, hidden }) => ({ key, w, h, hidden }));

export const isDefaultLayout = (layout: CardPlacement[], available: PlaceableCard[]) =>
  layout.length === available.length &&
  layout.every((placement, index) => {
    const card = available[index];
    return placement.key === card.key && placement.w === card.defaultSize.w && placement.h === card.defaultSize.h && !placement.hidden;
  });

const rowsOf =<T extends { w: number }>(cards: T[]): T[][] => {
  const rows: T[][] = [];
  let used = GRID_COLUMNS;

  for (const card of cards) {
    if (used + card.w > GRID_COLUMNS) {
      rows.push([]);
      used = 0;
    }
    rows[rows.length - 1].push(card);
    used += card.w;
  }

  return rows;
};

const fillRow = (widths: number[]): number[] => {
  const total = widths.reduce((sum, width) => sum + width, 0);
  const exact = widths.map(width => (width * GRID_COLUMNS) / total);
  const spans = exact.map(Math.floor);
  const spare = GRID_COLUMNS - spans.reduce((sum, span) => sum + span, 0);

  exact
    .map((value, index) => ({ index, remainder: value - spans[index] }))
    .sort((a, b) => b.remainder - a.remainder)
    .slice(0, spare)
    .forEach(({ index }) => { spans[index] += 1; });

  return spans;
};

export const arrangeCards = (cards: CardSize[], { editing }: { editing: boolean }): CardArrangement[] =>
  rowsOf(cards).flatMap(row => {
    if (editing) return row.map(card => ({ span: card.w, height: card.h }));

    const spans = fillRow(row.map(card => card.w));
    const height = row.reduce((tallest, card) => CARD_HEIGHT_PX[card.h] > CARD_HEIGHT_PX[tallest] ? card.h : tallest, row[0].h);
    return row.map((_, index) => ({ span: spans[index], height }));
  });

export const pxToSpan = (widthPx: number, rowWidthPx: number, gapPx: number) => {
  const column = (rowWidthPx - (GRID_COLUMNS - 1) * gapPx) / GRID_COLUMNS;
  return clampSpan(Math.round((widthPx + gapPx) / (column + gapPx)));
};

export const nearestHeight = (heightPx: number): CardHeight =>
  CARD_HEIGHTS.reduce((best, height) =>
    Math.abs(CARD_HEIGHT_PX[height] - heightPx) < Math.abs(CARD_HEIGHT_PX[best] - heightPx) ? height : best);

export const stepSpan = (span: number, delta: number) => clampSpan(span + delta);

export const stepHeight = (height: CardHeight, delta: number): CardHeight =>
  CARD_HEIGHTS[Math.min(CARD_HEIGHTS.length - 1, Math.max(0, CARD_HEIGHTS.indexOf(height) + delta))];

export const moveCard = <T extends { key: string }>(layout: T[], activeKey: string, overKey: string): T[] => {
  const from = layout.findIndex(card => card.key === activeKey);
  const to = layout.findIndex(card => card.key === overKey);
  if (from < 0 || to < 0 || from === to) return layout;

  const next = [...layout];
  next.splice(to, 0, ...next.splice(from, 1));
  return next;
};

export const moveItem = (keys: string[], activeKey: string, overKey: string) =>
  moveCard(keys.map(key => ({ key })), activeKey, overKey).map(item => item.key);

export const mergeItemOrder = (saved: string[] | undefined, shown: string[]): string[] => {
  const showing = new Set(shown);
  let placed = 0;
  const merged = [...new Set(saved ?? [])].map(key => (showing.has(key) ? shown[placed++] : key));

  return [...merged, ...shown.slice(placed)];
};
